import { useEffect, useRef, useState } from "react";
import type { CSSProperties, ReactElement } from "react";
import type { KundeListe as KundeListeEintrag, Unternehmen } from "@kundeninfo/api-client";
import { Icon, Objektliste, Objektzeile } from "@kundeninfo/ui";
import type { Objektmarke } from "@kundeninfo/ui";
import { markenTextfarben } from "../markenfarben.js";
import { relativeZeit } from "../format.js";
import { KiKnopf, Suchfeld } from "../components/meHuellen.js";
import "./KundenListe.css";

/** Schmales Fehlerbild — passt sowohl eine ApiError-Instanz als auch ein Test-Double. */
export type KundenFehler = { message: string; code?: string; incidentId?: string };

export type KundenListeProps = {
  items: KundeListeEintrag[];
  loading: boolean;
  error: KundenFehler | null;
  search: string;
  onSearch: (value: string) => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Der Einstieg zum Anlegen eines Kunden (Aufgabe 17). Ohne Angabe fehlt der Knopf. */
  onNeu?: () => void;
  /** Für die Kürzel-Chips in Markenfarbe. Ohne Angabe erscheinen sie neutral. */
  companies?: Unternehmen[];
  /**
   * Die DOM-Id des Suchfelds. Sie ist wählbar, seit die Liste ein ZWEITES Mal im selben
   * Dokument stehen kann (im Eltern-Modal des Konzern-Reiters): Zwei Elemente mit
   * derselben Id sind eines zu viel — `document.getElementById` träfe das falsche, und
   * ein Prüfer meldete das Dokument zu Recht als ungültig.
   */
  feldId?: string;
};

const SUCH_VERZOEGERUNG_MS = 250;

function chipStil(colorHex: string | undefined): CSSProperties | undefined {
  if (colorHex === undefined) return undefined;
  const { hell, dunkel } = markenTextfarben(colorHex);
  return {
    "--kl-chip-bg": `color-mix(in srgb, ${colorHex} 14%, var(--flaeche))`,
    "--kl-chip-rand": `color-mix(in srgb, ${colorHex} 40%, transparent)`,
    "--kl-chip-text-hell": hell,
    "--kl-chip-text-dunkel": dunkel,
  } as CSSProperties;
}

/** Was in der zweiten Zeile steht: Ort und — bei EINSEHBAREM Elternteil — „Tochter von …“. */
function zweiteZeile(item: KundeListeEintrag): string[] {
  const teile: string[] = [];
  if (item.city !== null && item.city !== undefined && item.city !== "") teile.push(item.city);
  if (item.parentName !== null && item.parentName !== undefined && item.parentName !== "") {
    teile.push(`Tochter von ${item.parentName}`);
  }
  return teile;
}

export function KundenListe(p: KundenListeProps): ReactElement {
  const companies = p.companies ?? [];
  const [eingabe, setEingabe] = useState(p.search);
  const letztGemeldet = useRef(p.search);

  // Ändert sich die Suche von außen (z. B. weil sie zurückgesetzt wurde), zieht das Feld nach.
  // NUR bei einer Änderung, nicht beim Einhängen (ME-Etappe 2, Fixrunde F2): Der Effekt ist
  // passiv und läuft nach einem Zeichnen ausserhalb von `act` erst nach dem Malen. Tippte
  // jemand in dieser Lücke, setzte das nachlaufende `setEingabe(p.search)` die Eingabe
  // wieder auf den alten Stand — die Suche ging verloren (belegt in
  // web/test/listenspalte.test.tsx, „Rückweg auf Kunden", unter Last rot).
  const gesehen = useRef(p.search);
  useEffect(() => {
    if (p.search === gesehen.current) return;
    gesehen.current = p.search;
    setEingabe(p.search);
    letztGemeldet.current = p.search;
  }, [p.search]);

  useEffect(() => {
    if (eingabe === letztGemeldet.current) return;
    const timer = setTimeout(() => {
      letztGemeldet.current = eingabe;
      p.onSearch(eingabe);
    }, SUCH_VERZOEGERUNG_MS);
    return () => clearTimeout(timer);
    // p.onSearch bewusst nicht in den Abhängigkeiten: Ein neuer Funktionswert bei jedem
    // Elternrender darf den laufenden Verzögerungs-Timer nicht neu starten.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eingabe]);

  return (
    <div className="kl">
      <div className="kl__suche">
        {/* ME-Etappe 4, P4: Suchfeld `me-eingabe art="suche"`, „Neuer Kunde“ `me-knopf`. */}
        <Suchfeld id={p.feldId ?? "kl-suchfeld"} name="Kunden durchsuchen"
          platzhalter="Firma oder Ort suchen" wert={eingabe} onText={setEingabe} />
        {p.onNeu !== undefined && (
          <KiKnopf art="primaer" className="kl__neu" onClick={p.onNeu}>Neuer Kunde</KiKnopf>
        )}
      </div>

      {p.error !== null && (
        <div className="kl__fehler" role="alert">
          <Icon name="triangle-exclamation" size={16} label="Fehler" />
          <span>{p.error.message}</span>
        </div>
      )}

      {/* Gegenstück zu role="alert" oben: role="status" kündigt sich Screenreadern von
          selbst an (implizites aria-live="polite"). p.loading kommt ausschließlich aus der
          echten Ladeoperation in App.tsx (dort erst nach der 250-ms-Verzögerung dieser
          Komponente gesetzt) — der Hinweis flackert deshalb nicht bei jedem Tastendruck. */}
      {p.error === null && p.loading && (
        <p className="kl__lade" role="status">
          <span className="kl__lade-spinner" aria-hidden="true" />
          <span>Lädt …</span>
        </p>
      )}

      {p.error === null && !p.loading && p.items.length === 0 && (
        <p className="kl__hinweis">Kein Kunde gefunden.</p>
      )}

      {/* Die Treffer als Objektliste (ME-Etappe 4, P4; Lagebild: Slots `chips`, `zusatz`):
          Name in Zeile 1, Ort und Konzernhinweis als `meta`, die Unternehmenskürzel in der
          Hausfarbe als Marken, die letzte Aktivität am Zeilenende. Roving Tabindex und
          Pfeiltasten macht das Element. Der zugängliche Name trägt den Konzernhinweis als
          WORT („Tochter von …“) — nur bei EINSEHBAREM Elternteil (Festlegung F1, Punkt 5),
          der Server lässt `parentName` sonst leer. */}
      {p.items.length > 0 && (
        <div className="kl__treffer">
          <Objektliste label="Kunden">
            {p.items.map((item) => {
              const zweite = zweiteZeile(item);
              const marken: Objektmarke[] = item.relations.map((r) => {
                const stil = chipStil(companies.find((c) => c.shortCode === r.shortCode)?.colorHex);
                return { text: r.shortCode, className: "kl__chip",
                         ...(stil === undefined ? {} : { style: stil }) };
              });
              return (
                <Objektzeile key={item.id} name={item.companyName}
                  {...(zweite.length === 0 ? {} : { meta: zweite.join(" · ") })}
                  marken={marken}
                  gewaehlt={item.id === p.selectedId}
                  ariaLabel={[`Kunde ${item.companyName}`, ...zweite, "anzeigen"].join(", ")}
                  onOeffnen={() => p.onSelect(item.id)}>
                  <span className="kl__aktivitaet">{relativeZeit(item.lastActivityAt)}</span>
                </Objektzeile>
              );
            })}
          </Objektliste>
        </div>
      )}
    </div>
  );
}
